import { Button, Kind, Size } from '@thanx/uikit/button'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import {
  ChannelAcceptance,
  LIVE_GREEN,
  NEEDS_ACTIVATION_GOLD,
  NEEDS_ACTIVATION_GOLD_TEXT,
} from './compatibility'

const t = buildTranslate('promo_codes.channel_status')

const DOT_SIZE = 7

type Channel = 'online' | 'pos'

// Dot and text can carry different colors: the dot is a graphical object (WCAG
// 3:1 bar), the label is normal-size text (4.5:1 bar). needs_activation and
// pos_coming_soon diverge — a lighter graphical dot with AA-compliant darker
// text (gold for activation, grey90 for coming soon).
const STATE_STYLE: Record<ChannelAcceptance, { dot: string; text: string }> = {
  enabled: { dot: LIVE_GREEN, text: LIVE_GREEN },
  needs_activation: {
    dot: NEEDS_ACTIVATION_GOLD,
    text: NEEDS_ACTIVATION_GOLD_TEXT,
  },
  disabled_in_template: { dot: palette.grey70, text: palette.grey70 },
  pos_coming_soon: { dot: palette.grey70, text: palette.grey90 },
  pos_not_supported: { dot: palette.grey70, text: palette.grey70 },
  provider_unavailable: { dot: palette.grey70, text: palette.grey70 },
}

// Copy is channel-specific only for the unavailable case (online vs. POS
// wording); the other states share a label across both channels.
// `pos_not_supported` is only ever produced for the POS channel.
const statusLabel = (channel: Channel, state: ChannelAcceptance): string => {
  if (state === 'provider_unavailable') {
    return channel === 'online' ? t('online_unavailable') : t('pos_unavailable')
  }
  return t(state)
}

type Props = {
  channel: Channel
  state: ChannelAcceptance
  // Provided only where an in-app activation destination exists (the Olo
  // Config tab on Landing). Absent → the "needs activation" state is plain text.
  onActivate?: () => void
}

const ChannelStatusCell: React.FC<Props> = ({ channel, state, onActivate }) => {
  const [css] = useStyletron()
  const style = STATE_STYLE[state]

  return (
    <span
      data-testid={`channel-status-${channel}`}
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
      })}
    >
      <span
        aria-hidden="true"
        className={css({
          flexShrink: 0,
          width: `${DOT_SIZE}px`,
          height: `${DOT_SIZE}px`,
          borderRadius: '50%',
          backgroundColor: style.dot,
        })}
      />
      <span className={`body-text-4 ${css({ color: style.text })}`}>
        {statusLabel(channel, state)}
      </span>
      {state === 'needs_activation' && onActivate && (
        <Button
          kind={Kind.MINIMAL}
          size={Size.SMALL}
          onClick={onActivate}
          aria-label={t(
            channel === 'pos' ? 'activate_pos_aria' : 'activate_online_aria'
          )}
          data-testid={`channel-activate-${channel}`}
        >
          {t('activate')}
        </Button>
      )}
    </span>
  )
}

export default ChannelStatusCell
